import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountingCategory, SearchableSelectTypes } from '../../models/common';
import { UtilityService } from 'src/app/utils/utility.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { DepositProductService } from 'src/app/pages/settings/services/deposit-product.service';
import { WizerValidatorService } from 'src/app/utils/wizer.validators.service';
import { DragDropListComponent } from '../../components/drag-drop-list/drag-drop-list.component';
import { FeeChargeRow, FeeChargeConfigComponent } from '../../components/fee-charge-config/fee-charge-config.component';
import { CurrencyFormatterService } from '../../services/currency-formatter.service';
import { FeeEventOption, FeeEventType } from '../../models/fee-config';
import {
  DepositProductFeeCharge,
  DepositProductAccountingRow,
} from '../../models/deposit-product.model';
import { ProceedModalComponent } from '../proceed-modal/proceed-modal.component';
import { ReasonModalComponent } from '../reason-modal/reason-modal.component';
import { EMPTY, merge } from 'rxjs';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';
import {
  INTEREST_PERIOD_OPTIONS,
  TENURE_UNIT_OPTIONS,
  compoundingForTenure,
  compoundingOptionsFor,
  tenureUnitForFrequency,
  tenureUnitLabel,
  tenureUnitsForFrequency,
} from 'src/app/utils/deposit-tenure';

interface RecurringCalculatorResult {
  numberOfContributionPeriods: number | null;
  contributionPerPeriod: number | null;
  totalContributions: number | null;
  projectedInterestEarned: number | null;
  completionBonus: number | null;
  projectedBalanceAtCompletion: number | null;
}

interface FixedDepositCalculatorResult {
  numberOfCompoundingPeriods: number | null;
  durationInDays: number | null;
  maturityDate: string | null;
  interestToMaturity: number | null;
  maturityValue: number | null;
  earlyWithdrawalInterest: number | null;
  earlyWithdrawalPenalty: number | null;
  earlyWithdrawalPayout: number | null;
}

@Component({
  selector: 'app-new-view-deposit-product',
  templateUrl: './new-view-deposit-product.component.html',
  styleUrls: ['./new-view-deposit-product.component.css']
})
export class NewViewDepositProductComponent implements OnInit {

  @ViewChild(DragDropListComponent, { static: true }) dragDrop: DragDropListComponent;
  @ViewChild(FeeChargeConfigComponent) feeChargeConfig: FeeChargeConfigComponent;
  public readonly activeAccountType = 'CASH';

  public charges: any[];
  public glAccounts: any[];
  public fundSources: any[] = [];

  public basicForm!: FormGroup;
  public interestExpenseForm!: FormGroup;
  public otherInfoForm!: FormGroup;
  public productInfoForm!: FormGroup;
  public channelsForm!: FormGroup;
  public fixedDepositForm!: FormGroup;
  public recurringSavingsForm!: FormGroup;
  public productTypeControl = new FormControl('DEPOSIT_ACCOUNT', Validators.required);
  public selectedProductType = 'DEPOSIT_ACCOUNT';
  public allowOverdraftControl = new FormControl(true);
  public readonly tenureUnitOptions = TENURE_UNIT_OPTIONS;
  public readonly interestPeriodOptions = INTEREST_PERIOD_OPTIONS;
  public readonly fixedTierRows = [
    { label: $localize`:@@default:Default`, amount: 'defaultAmount', duration: 'defaultDurationDays', rate: 'defaultInterestRate', editablePeriods: true },
    { label: $localize`:@@minimum:Minimum`, amount: 'minimumAmount', duration: 'minimumDurationDays', rate: 'minimumInterestRate', editablePeriods: false },
    { label: $localize`:@@maximum:Maximum`, amount: 'maximumAmount', duration: 'maximumDurationDays', rate: 'maximumInterestRate', editablePeriods: false },
  ];
  public readonly recurringTierRows = [
    { label: $localize`:@@default:Default`, amount: 'defaultTargetAmount', duration: 'defaultDurationMonths', rate: 'defaultInterestRate', editablePeriods: true },
    { label: $localize`:@@minimum:Minimum`, amount: 'minimumTargetAmount', duration: 'minimumDurationMonths', rate: 'minimumInterestRate', editablePeriods: false },
    { label: $localize`:@@maximum:Maximum`, amount: 'maximumTargetAmount', duration: 'maximumDurationMonths', rate: 'maximumInterestRate', editablePeriods: false },
  ];

  public isViewMode: boolean = false
  public canEdit: boolean = true;
  public showApprovalActions: boolean = false;
  private loadProductOnInit: boolean = true;
  public isDisabled: boolean = false;
  public isFormValid: boolean = false;
  private selectedDepositProductId: number;
  private existingProduct: any = {};
  public feeChargeRows: FeeChargeRow[] = [];
  public feeEventTypes: FeeEventOption[] = [];
  public currencyCode: string;
  public depositProjectedInterest: number | null = null;
  public fixedDepositCalculatorResult: FixedDepositCalculatorResult | null = null;
  public recurringCalculatorResult: RecurringCalculatorResult | null = null;
  public readonly feeChargeNameHeader = $localize`:@@fee_charge_name:Fee/Charge Name`;

  public readonly channelOptions = [
    {
      control: 'webApp',
      icon: 'assets/icons/webAppIcon.svg',
      label: $localize`:@@web_app:Web App`,
    },
    {
      control: 'mobileApp',
      icon: 'assets/icons/mobileAppIcon.svg',
      label: $localize`:@@mobile_app:Mobile App`,
    },
    {
      control: 'ussd',
      icon: 'assets/icons/mobileAppIcon.svg',
      label: $localize`:@@ussd:USSD`,
    },
  ];

  public readonly calculationBases = [
    { value: 'WORKING', label: $localize`:@@working_days_option:Working Days` },
    { value: 'CALENDAR', label: $localize`:@@calender_days:Calendar Days` },
  ];

  public readonly weekendOptions = [
    { value: 'SATURDAY_SUNDAY', label: $localize`:@@weekend_sat_sun:Saturday, Sunday` },
    { value: 'SUNDAY_MONDAY', label: $localize`:@@weekend_sun_mon:Sunday, Monday` },
    { value: 'MONDAY_TUESDAY', label: $localize`:@@weekend_mon_tue:Monday, Tuesday` },
    { value: 'TUESDAY_WEDNESDAY', label: $localize`:@@weekend_tue_wed:Tuesday, Wednesday` },
    { value: 'WEDNESDAY_THURSDAY', label: $localize`:@@weekend_wed_thu:Wednesday, Thursday` },
    { value: 'THURSDAY_FRIDAY', label: $localize`:@@weekend_thu_fri:Thursday, Friday` },
    { value: 'FRIDAY_SATURDAY', label: $localize`:@@weekend_fri_sat:Friday, Saturday` },
    { value: 'SATURDAY', label: $localize`:@@weekend_sat_only:Saturday only` },
    { value: 'SUNDAY', label: $localize`:@@weekend_sun_only:Sunday only` },
    { value: 'MONDAY', label: $localize`:@@weekend_mon_only:Monday only` },
    { value: 'TUESDAY', label: $localize`:@@weekend_tue_only:Tuesday only` },
    { value: 'WEDNESDAY', label: $localize`:@@weekend_wed_only:Wednesday only` },
    { value: 'THURSDAY', label: $localize`:@@weekend_thu_only:Thursday only` },
    { value: 'FRIDAY', label: $localize`:@@weekend_fri_only:Friday only` },
  ];

  public currencies = [
    { code: 'NGN', name: 'NGN' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'EUR', name: 'EUR' },
  ];

  public events: { label: string, value: string }[] = [
    { label: $localize`:@@deposit:Deposit`, value: 'DEPOSIT' },
    { label: $localize`:@@withdrawal:Withdrawal`, value: 'WITHDRAWAL' }
  ];
  public eventMaps: any[] = [];
  public accountingCategories: { key: string; value: AccountingCategory }[] = [
    { key: 'deposit', value: { name: $localize`:@@deposit:Deposit`, row: [] } },
    { key: 'withdrawal', value: { name: $localize`:@@withdrawal:Withdrawal`, row: [] } },
    { key: 'fees_charges', value: { name: $localize`:@@fees_slash_charges:Fees/Charges`, row: [] } },
    { key: 'interest', value: { name: $localize`:@@interest:Interest`, row: [] } },
  ]

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialoRef: MatDialogRef<NewViewDepositProductComponent>,
    private dialog: MatDialog,
    private utils: UtilityService,
    private toast: NotificationService,
    private depositProductService: DepositProductService,
    private validator: WizerValidatorService,
    private currencyFormatter: CurrencyFormatterService,
  ) {
    this.currencyCode = this.currencyFormatter.getConfig().currencyCode || 'NGN';

    if (data) {

      this.isViewMode = data.isViewMode;
      this.canEdit = data.canEdit !== false;
      this.showApprovalActions = data.showApprovalActions === true;
      this.loadProductOnInit = data.loadProductOnInit !== false;
      this.charges = data.charges;
      this.eventMaps = data.eventMaps;

      if (data.accountingData) {
        const { ASSET, INCOME, EXPENSE, LIABILITY, EQUITY } = data.accountingData;

        // Check if each property exists before spreading them
        const assetAccounts = ASSET ? [...ASSET] : [];
        const incomeAccounts = INCOME ? [...INCOME] : [];
        const expenseAccounts = EXPENSE ? [...EXPENSE] : [];
        const liabilityAccounts = LIABILITY ? [...LIABILITY] : [];
        const equityAccounts = EQUITY ? [...EQUITY] : [];

        this.fundSources = assetAccounts;
        this.glAccounts = [...assetAccounts, ...incomeAccounts, ...expenseAccounts, ...liabilityAccounts, ...equityAccounts].sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    this.initForm()
    this.setupSwitchDependencies();
    if (data && this.isViewMode) {
      this.existingProduct = data.depositProduct ?? {};
      const {
        id,
        name,
        code,
        description,
        productType,
        currency,

        overdraftLimit,
        overdraftInterestRate,
        overdraftInterestPeriodUnit,
        overdraftCompoundingPeriodUnit,
        overdraftInterestPostingPeriodUnit,
        overdraftInterestCalculationType,

        interestRate,
        interestRatePeriodUnit,
        compoundingPeriodUnit,
        interestPostingPeriodUnit,
        interestCalculationType,

        savingsProductCharges,
        minimumOpeningBalance,
        transferLimit,
        dailyTransferLimit,
        roundingMultiple,
        dormancyThreshold,

        savingsProductAccountings
      } = data.depositProduct as {
        id: number;
        name: string;
        code: string;
        description: string;
        productType: string;
        currency?: string;
        overdraftLimit: any;
        overdraftInterestRate: any;
        overdraftInterestPeriodUnit: any;
        overdraftCompoundingPeriodUnit: any;
        overdraftInterestPostingPeriodUnit: any;
        overdraftInterestCalculationType: any;
        interestRate: any;
        interestRatePeriodUnit: any;
        compoundingPeriodUnit: any;
        interestPostingPeriodUnit: any;
        interestCalculationType: any;
        savingsProductCharges: DepositProductFeeCharge[];
        minimumOpeningBalance: any;
        transferLimit: any;
        dailyTransferLimit: any;
        roundingMultiple: any;
        dormancyThreshold: any;
        savingsProductAccountings: DepositProductAccountingRow[];
      }

      const normalizedProductType = this.normalizeProductType(productType ?? this.existingProduct.type);
      const product = data.depositProduct;
      const productCharges: DepositProductFeeCharge[] = savingsProductCharges ?? product.charges ?? [];
      const productAccountings: DepositProductAccountingRow[] = savingsProductAccountings
        ?? product.depositProductAccounting
        ?? product.savingsProductAccounting
        ?? [];

      this.selectedDepositProductId = id
      this.productTypeControl.setValue(normalizedProductType);
      this.selectedProductType = normalizedProductType;
      this.basicForm.patchValue({ name, code, description, currency: currency || this.currencyCode, overdraftLimit, dormancyThreshold });
      this.allowOverdraftControl.setValue(
        product.allowOverdraft == null
          ? Boolean(overdraftLimit)
          : this.booleanValue(product.allowOverdraft)
      );
      this.patchDepositAccountInterestFields(product);
      this.otherInfoForm.patchValue({
        chargeIds: productCharges?.map(charges => charges?.charge?.id ?? charges?.chargeId),
        openingBalance: product.minimumBalance ?? product.openingBalance ?? minimumOpeningBalance,
        transferLimit,
        dailyTransferLimit
      })

      const productFundSources = product.fundSources
        ?? product.collectionAccounts
        ?? product.savingsReferences
        ?? [];
      this.productInfoForm.patchValue({
        fundSources: productFundSources
          .map((source: any) => source?.accountId ?? source?.glAccountId ?? source?.id)
          .map((id: any) => this.numberValue(id, 'plain'))
          .filter((id: any) => id != null),
        calculationBasis: this.booleanValue(product.isCalenderDays) ? 'CALENDAR' : 'WORKING',
        weekends: Array.isArray(product.weekendDays)
          ? product.weekendDays.join('_')
          : 'SATURDAY_SUNDAY',
        roundingMultiple,
      });
      this.channelsForm.patchValue({
        webApp: this.booleanValue(product.enableWebClients ?? product.enableWebclient ?? product.enableClients),
        mobileApp: this.booleanValue(product.enableMobileClients),
        ussd: this.booleanValue(product.enableUssdClients),
      });

      // Populate variant-specific forms
      if (normalizedProductType === 'FIXED_DEPOSIT') {
        this.fixedDepositForm.patchValue({
          durationPeriodUnit: product.fixedDepositDurationPeriodUnit ?? 'DAY',
          defaultAmount: product.fixedDepositDefaultAmount ?? product.defaultAmount,
          minimumAmount: product.fixedDepositMinimumAmount ?? product.minimumAmount,
          maximumAmount: product.fixedDepositMaximumAmount ?? product.maximumAmount,
          defaultDurationDays: product.fixedDepositDefaultDuration ?? product.defaultDurationDays,
          minimumDurationDays: product.fixedDepositMinimumDuration ?? product.minimumDurationDays,
          maximumDurationDays: product.fixedDepositMaximumDuration ?? product.maximumDurationDays,
          defaultInterestRate: product.fixedDepositDefaultInterestRate ?? product.defaultInterestRate,
          minimumInterestRate: product.fixedDepositMinimumInterestRate ?? product.minimumInterestRate,
          maximumInterestRate: product.fixedDepositMaximumInterestRate ?? product.maximumInterestRate,
          interestRatePeriodUnit: product.fixedDepositInterestPeriodUnit ?? product.interestRatePeriodUnit ?? 'MONTH',
          compoundingFrequency: product.fixedDepositCompoundingPeriodUnit ?? product.compoundingFrequency,
          interestPayout: this.toUiPayoutPeriod(product.fixedDepositInterestPayoutPeriodUnit ?? product.interestPayout),
          autoRollover: this.booleanValue(
            product.fixedDepositAutoRolloverAtMaturity ?? product.autoRollover,
            true
          ),
          rolloverBasis: product.fixedDepositRolloverBasis ?? product.rolloverBasis,
          gracePeriod: product.fixedDepositRolloverGracePeriodDays ?? product.gracePeriod,
          allowPrematureWithdrawal: this.booleanValue(
            product.fixedDepositAllowPrematureWithdrawal ?? product.allowPrematureWithdrawal,
            true
          ),
          minimumHoldingPeriod: product.fixedDepositMinimumHoldingPeriodDays ?? product.minimumHoldingPeriod,
          prematureWithdrawalPenaltyType: product.fixedDepositPrematureWithdrawalPenaltyType ?? 'PERCENTAGE',
          penaltyOnInterest: product.fixedDepositPrematureWithdrawalPenaltyOnInterest ?? product.penaltyOnInterest,
          noticePeriod: product.fixedDepositPrematureWithdrawalNoticePeriodDays ?? product.noticePeriod,
        });
      } else if (normalizedProductType === 'RECURRING_TARGETED') {
        this.recurringSavingsForm.patchValue({
          durationPeriodUnit: product.recurringDurationPeriodUnit ?? 'MONTH',
          defaultTargetAmount: product.recurringDefaultTargetAmount ?? product.defaultTargetAmount,
          minimumTargetAmount: product.recurringMinimumTargetAmount ?? product.minimumTargetAmount,
          maximumTargetAmount: product.recurringMaximumTargetAmount ?? product.maximumTargetAmount,
          defaultDurationMonths: product.recurringDefaultDuration ?? product.defaultDurationMonths,
          minimumDurationMonths: product.recurringMinimumDuration ?? product.minimumDurationMonths,
          maximumDurationMonths: product.recurringMaximumDuration ?? product.maximumDurationMonths,
          defaultInterestRate: product.recurringDefaultInterestRate ?? product.defaultInterestRate,
          minimumInterestRate: product.recurringMinimumInterestRate ?? product.minimumInterestRate,
          maximumInterestRate: product.recurringMaximumInterestRate ?? product.maximumInterestRate,
          interestRatePeriodUnit: product.recurringInterestPeriodUnit ?? product.interestRatePeriodUnit ?? 'MONTH',
          compoundingFrequency: product.recurringCompoundingPeriodUnit ?? product.compoundingFrequency,
          interestPayout: this.toUiPayoutPeriod(product.recurringInterestPayoutPeriodUnit ?? product.interestPayout),
          savingsFrequency: this.toUiSavingsFrequency(product.recurringSavingsFrequency ?? product.savingsFrequency),
          recurringDayOfMonth: product.recurringDayOfMonth
            ?? product.savingsDayOfMonth
            ?? product.dayOfMonth
            ?? product.contributionDayOfMonth
            ?? 3,
          contributionSource: this.toUiContributionSource(product.recurringContributionSource ?? product.contributionSource),
          gracePeriod: product.recurringGracePeriodDays ?? product.gracePeriod,
          awardCompletionBonus: this.booleanValue(
            product.recurringBonusOnCompletionEnabled ?? product.awardCompletionBonus,
            true
          ),
          bonusType: product.recurringBonusType ?? product.bonusType,
          bonusValue: product.recurringBonusValue ?? product.bonusValue,
          allowEarlyWithdrawal: this.booleanValue(
            product.recurringAllowEarlyWithdrawal ?? product.allowEarlyWithdrawal,
            true
          ),
          earlyWithdrawalPenaltyType: product.recurringEarlyWithdrawalPenaltyType ?? product.earlyWithdrawalPenaltyType,
          earlyWithdrawalPenaltyValue: product.recurringEarlyWithdrawalPenaltyValue ?? product.earlyWithdrawalPenaltyValue,
          maximumSavedPeriodDays: product.recurringEarlyWithdrawalMinimumLockPeriodDays ?? product.maximumSavedPeriodDays,
          forfeitBonusOnEarlyWithdrawal: this.booleanValue(product.forfeitBonusOnEarlyWithdrawal, true),
          missedContributionPenaltyType: product.recurringMissedContributionPenaltyType ?? product.missedContributionPenaltyType,
          missedContributionPenaltyValue: product.recurringMissedContributionPenaltyValue ?? product.missedContributionPenaltyValue,
          forfeitBonusOnMissedContribution: this.booleanValue(
            product.recurringForfeitBonusOnMissedContribution ?? product.forfeitBonusOnMissedContribution,
            true
          ),
          glTemplate: this.toUiCompletionAction(
            product.recurringAtCompletionAction ?? product.glTemplate
          ),
        });
      }

      if (productCharges?.length) {
        this.feeChargeRows = [...productCharges]
          .sort((firstCharge, secondCharge) => firstCharge.sortOrder - secondCharge.sortOrder)
          .map((charge: DepositProductFeeCharge) => this.mapApiChargeToRow(charge));
      }

      // Group the deposirProductAccounting objects by groupLabel
      const groupedData = productAccountings.reduce((result, obj) => {
        if (!result[obj.groupLabel]) {
          result[obj.groupLabel] = [];
        }
        result[obj.groupLabel].push(obj);
        return result;
      }, {});



      // Map the grouped data to accountingCategories and format eventName
      const updatedAccountingCategories = this.accountingCategories.map(category => {
        const matchingGroup = groupedData[category.value.name];

        if (matchingGroup) {
          category.value.row = matchingGroup.map((row: DepositProductAccountingRow) => {
            const selectedMapping = this.eventMaps.find(item => item.mapping_name.toLowerCase() === row.eventName.toLowerCase());

            // Seed from the row's own GL name fields so display is not limited
            // by what the useInDepositSetting-filtered glAccounts contains
            const debitSeed = row.debitGlId && row.debitGlName
              ? [{ id: row.debitGlId, name: row.debitGlName }]
              : (this.glAccounts?.filter(g => g.id === row.debitGlId) ?? []);
            const creditSeed = row.creditGlId && row.creditGlName
              ? [{ id: row.creditGlId, name: row.creditGlName }]
              : (this.glAccounts?.filter(g => g.id === row.creditGlId) ?? []);

            // fees_charges rows: reconstruct combined "EVENTNAME-EVENTMAPPING" to match dropdown option values
            if (category.key === 'fees_charges') {
              return {
                ...row,
                eventName: `${row.eventName}-${row.eventMapping}`,
                eventNameList: [...new Set(this.eventMaps?.flatMap(item => item.mappings.split(', ')) ?? [])],
                creditGlAccountList: creditSeed,
                debitGlAccountList: debitSeed
              };
            }

            return {
              ...row,
              eventName: `${this.utils.underScoreToSpace(row.eventName)}-${this.utils.underScoreToSpace(row.eventMapping)}`,
              eventMapping: this.utils.titleCase(this.utils.underScoreToSpace(row.eventMapping)),
              eventMappingList: selectedMapping ? selectedMapping.mappings.split(', ') : [],
              eventNameList: selectedMapping ? selectedMapping.mappings.split(', ') : [],
              creditGlAccountList: creditSeed,
              debitGlAccountList: debitSeed
            };
          });
        }

        return category;
      });



      // Assign the updated categories back to accountingCategories
      this.accountingCategories = updatedAccountingCategories;

      this.disableAllFields();
    }
  }

  initForm() {
    this.initBasicForm();
    this.initInterestExpenseForm();
    this.initOtherInfoForm();
    this.initProductInfoForm();
    this.initChannelsForm();
    this.initFixedDepositForm();
    this.initRecurringSavingsForm();
  }

  onProductTypeChange(productType: string) {
    this.selectedProductType = this.normalizeProductType(productType);
    if (this.selectedProductType !== 'RECURRING_TARGETED') {
      this.recurringCalculatorResult = null;
    }
    if (this.selectedProductType !== 'FIXED_DEPOSIT') {
      this.fixedDepositCalculatorResult = null;
    }
    const depositOnlyControls = [
      { control: this.basicForm.get('overdraftLimit'), validators: [Validators.required] },
      {
        control: this.basicForm.get('dormancyThreshold'),
        validators: [Validators.required, Validators.min(1)],
      },
    ];
    depositOnlyControls.forEach(({ control, validators }) => {
      if (this.selectedProductType === 'DEPOSIT_ACCOUNT') {
        control?.setValidators(validators);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  get overdraftLimitControl(): FormControl {
    return this.basicForm.get('overdraftLimit') as FormControl;
  }

  get dormancyThresholdControl(): FormControl {
    return this.basicForm.get('dormancyThreshold') as FormControl;
  }

  initBasicForm() {
    this.basicForm = this.fb.group({
      name: new FormControl('', [Validators.required]),
      code: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      currency: new FormControl(this.currencyCode, [Validators.required]),
      overdraftLimit: new FormControl('', Validators.required),
      dormancyThreshold: new FormControl('', [Validators.required, Validators.min(1)]),
    });
  }

  initInterestExpenseForm() {
    const percentageValidators = [
      Validators.required,
      Validators.pattern(/^(\d{1,2}(\.\d+)?|100(\.0+)?)$/),
    ];
    this.interestExpenseForm = this.fb.group({
      interestRate: new FormControl('', percentageValidators),
      minimumInterestRate: new FormControl('', percentageValidators),
      maximumInterestRate: new FormControl('', percentageValidators),
      interestPeriodUnit: new FormControl('MONTH', [Validators.required]),
      compoundingPeriodUnit: new FormControl('MONTH', [Validators.required]),
      interestPostingPeriodUnit: new FormControl('MONTH', [Validators.required]),
      interestCalculationType: new FormControl('AVERAGE_BALANCE', [Validators.required]),

      overdraftInterestRate: new FormControl('', percentageValidators),
      minimumOverdraftInterestRate: new FormControl('', percentageValidators),
      maximumOverdraftInterestRate: new FormControl('', percentageValidators),
      overdraftInterestPeriodUnit: new FormControl('MONTH', [Validators.required]),
      overdraftCompoundingPeriodUnit: new FormControl('MONTH', [Validators.required]),
      overdraftInterestPostingPeriodUnit: new FormControl('MONTH', [Validators.required]),
      overdraftInterestCalculationType: new FormControl('AVERAGE_BALANCE', [Validators.required]),

      calculatorAverageBalance: new FormControl(''),
      calculatorInterestRate: new FormControl(''),
      calculatorPeriod: new FormControl(''),
    });
  }

  initOtherInfoForm() {
    this.otherInfoForm = this.fb.group({
      chargeIds: new FormControl([]),
      openingBalance: new FormControl('', [Validators.required]),
      transferLimit: new FormControl('', [Validators.required]),
      dailyTransferLimit: new FormControl('', [Validators.required]),
    });
  }

  initProductInfoForm() {
    this.productInfoForm = this.fb.group({
      fundSources: new FormControl([], [Validators.required]),
      calculationBasis: new FormControl('WORKING'),
      weekends: new FormControl('SATURDAY_SUNDAY'),
      roundingMultiple: new FormControl(1, [Validators.required]),
    });
  }

  initChannelsForm() {
    this.channelsForm = this.fb.group({
      webApp: new FormControl(true),
      mobileApp: new FormControl(true),
      ussd: new FormControl(false),
    });
  }

  get isWorkingDaysBasis(): boolean {
    return this.productInfoForm?.get('calculationBasis')?.value === 'WORKING';
  }

  removeFundSource(id: number, event: MouseEvent): void {
    event.stopPropagation();
    const control = this.productInfoForm.get('fundSources');
    control?.setValue((control.value || []).filter((value: number) => value !== id));
  }

  fundSourceName(id: number): string {
    const normalizedId = this.numberValue(id, 'plain');
    const existingSources = this.existingProduct.collectionAccounts
      ?? this.existingProduct.fundSources
      ?? this.existingProduct.savingsReferences
      ?? [];
    return this.fundSources.find(source => this.numberValue(source.id, 'plain') === normalizedId)?.name
      ?? existingSources.find((source: any) =>
        this.numberValue(source.accountId ?? source.glAccountId ?? source.id, 'plain') === normalizedId
      )?.accountName
      ?? existingSources.find((source: any) =>
        this.numberValue(source.accountId ?? source.glAccountId ?? source.id, 'plain') === normalizedId
      )?.glAccountName
      ?? existingSources.find((source: any) =>
        this.numberValue(source.accountId ?? source.glAccountId ?? source.id, 'plain') === normalizedId
      )?.name
      ?? '';
  }

  initFixedDepositForm() {
    this.fixedDepositForm = this.fb.group({
      durationPeriodUnit: ['DAY', Validators.required],
      defaultAmount: ['', Validators.required],
      minimumAmount: ['', Validators.required],
      maximumAmount: ['', Validators.required],
      defaultDurationDays: ['', Validators.required],
      minimumDurationDays: ['', Validators.required],
      maximumDurationDays: ['', Validators.required],
      defaultInterestRate: ['', Validators.required],
      minimumInterestRate: ['', Validators.required],
      maximumInterestRate: ['', Validators.required],
      interestRatePeriodUnit: ['MONTH', Validators.required],
      compoundingFrequency: ['MONTH', Validators.required],
      interestPayout: ['MONTH', Validators.required],
      autoRollover: [true],
      rolloverBasis: ['PRINCIPAL_AND_INTEREST', Validators.required],
      gracePeriod: ['', Validators.required],
      allowPrematureWithdrawal: [true],
      minimumHoldingPeriod: ['', Validators.required],
      prematureWithdrawalPenaltyType: ['PERCENTAGE', Validators.required],
      penaltyOnInterest: ['', Validators.required],
      noticePeriod: [''],
      calculatorAmount: [''],
      calculatorDuration: [''],
      calculatorInterestRate: [''],
      earlyWithdrawalDay: [''],
    });
  }

  initRecurringSavingsForm() {
    this.recurringSavingsForm = this.fb.group({
      durationPeriodUnit: ['MONTH', Validators.required],
      defaultTargetAmount: ['', Validators.required],
      minimumTargetAmount: ['', Validators.required],
      maximumTargetAmount: ['', Validators.required],
      defaultDurationMonths: ['', Validators.required],
      minimumDurationMonths: ['', Validators.required],
      maximumDurationMonths: ['', Validators.required],
      defaultInterestRate: ['', Validators.required],
      minimumInterestRate: ['', Validators.required],
      maximumInterestRate: ['', Validators.required],
      interestRatePeriodUnit: ['MONTH', Validators.required],
      compoundingFrequency: ['MONTH', Validators.required],
      interestPayout: ['MONTH', Validators.required],
      savingsFrequency: ['MONTHLY', Validators.required],
      recurringDayOfMonth: [3, [Validators.required, Validators.min(1), Validators.max(31)]],
      contributionSource: ['AUTO_DEBIT', Validators.required],
      gracePeriod: [3, Validators.required],
      awardCompletionBonus: [true],
      bonusType: ['PERCENTAGE'],
      bonusValue: [1],
      allowEarlyWithdrawal: [true],
      earlyWithdrawalPenaltyType: ['PERCENTAGE'],
      earlyWithdrawalPenaltyValue: [1],
      maximumSavedPeriodDays: [''],
      forfeitBonusOnEarlyWithdrawal: [true],
      missedContributionPenaltyType: ['PERCENTAGE'],
      missedContributionPenaltyValue: [1],
      forfeitBonusOnMissedContribution: [true],
      glTemplate: ['USE_LINKED_SAVINGS_ACCOUNT'],
      calculatorTargetAmount: [''],
      calculatorDuration: [''],
      calculatorInterestRate: [''],
    });
  }

  /**
   * Toggle-dependent controls follow the same behaviour as the loan product
   * form: switching a feature off disables the fields that configure it.
   */
  private setupSwitchDependencies(): void {
    this.bindSwitch(this.allowOverdraftControl, [
      { form: this.basicForm, names: ['overdraftLimit'] },
      {
        form: this.interestExpenseForm,
        names: [
          'overdraftInterestRate',
          'minimumOverdraftInterestRate',
          'maximumOverdraftInterestRate',
          'overdraftInterestPeriodUnit',
          'overdraftCompoundingPeriodUnit',
          'overdraftInterestPostingPeriodUnit',
          'overdraftInterestCalculationType',
        ],
      },
    ]);

    this.bindSwitch(this.fixedDepositForm.get('autoRollover'), [
      { form: this.fixedDepositForm, names: ['rolloverBasis', 'gracePeriod'] },
    ]);

    this.bindSwitch(this.fixedDepositForm.get('allowPrematureWithdrawal'), [
      {
        form: this.fixedDepositForm,
        names: ['minimumHoldingPeriod', 'penaltyOnInterest', 'noticePeriod'],
      },
    ]);

    this.bindSwitch(this.recurringSavingsForm.get('awardCompletionBonus'), [
      { form: this.recurringSavingsForm, names: ['bonusType', 'bonusValue'] },
    ]);

    const savingsFrequency = this.recurringSavingsForm.get('savingsFrequency');
    const recurringDayOfMonth = this.recurringSavingsForm.get('recurringDayOfMonth');
    const recurringDurationPeriodUnit = this.recurringSavingsForm.get('durationPeriodUnit');
    const applySavingsFrequencyState = (frequency: string) => {
      // the tenure cannot be shorter than the saving frequency
      const tenureUnit = recurringDurationPeriodUnit?.value;
      const raisedUnit = tenureUnitForFrequency(tenureUnit, frequency);
      if (raisedUnit !== tenureUnit) {
        recurringDurationPeriodUnit?.setValue(raisedUnit, { emitEvent: false });
        this.clampCompoundingToTenure(this.recurringSavingsForm);
      }
      if (frequency === 'MONTHLY') {
        recurringDayOfMonth?.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(31),
        ]);
      } else {
        recurringDayOfMonth?.clearValidators();
      }
      recurringDayOfMonth?.updateValueAndValidity({ emitEvent: false });
    };
    savingsFrequency?.valueChanges.subscribe(applySavingsFrequencyState);
    applySavingsFrequencyState(savingsFrequency?.value);

    this.bindSwitch(this.recurringSavingsForm.get('allowEarlyWithdrawal'), [
      {
        form: this.recurringSavingsForm,
        names: [
          'earlyWithdrawalPenaltyType',
          'earlyWithdrawalPenaltyValue',
          'maximumSavedPeriodDays',
          'forfeitBonusOnEarlyWithdrawal',
        ],
      },
    ]);
  }

  private bindSwitch(
    toggle: any,
    groups: { form: FormGroup; names: string[] }[]
  ): void {
    const applyState = (enabled: boolean) => {
      groups.forEach(group =>
        group.names.forEach(name => {
          const control = group.form.get(name);
          if (enabled) {
            control?.enable({ emitEvent: false });
          } else {
            control?.disable({ emitEvent: false });
          }
        })
      );
    };

    toggle?.valueChanges.subscribe((enabled: boolean) =>
      applyState(Boolean(enabled))
    );
    applyState(Boolean(toggle?.value));
  }

  /** Reapply dependent states after a form-wide enable operation. */
  private refreshSwitchDependencies(): void {
    const setState = (form: FormGroup, enabled: boolean, names: string[]) =>
      names.forEach(name => {
        const control = form.get(name);
        enabled
          ? control?.enable({ emitEvent: false })
          : control?.disable({ emitEvent: false });
      });

    setState(this.basicForm, Boolean(this.allowOverdraftControl.value), [
      'overdraftLimit',
    ]);
    setState(
      this.interestExpenseForm,
      Boolean(this.allowOverdraftControl.value),
      [
        'overdraftInterestRate',
        'minimumOverdraftInterestRate',
        'maximumOverdraftInterestRate',
        'overdraftInterestPeriodUnit',
        'overdraftCompoundingPeriodUnit',
        'overdraftInterestPostingPeriodUnit',
        'overdraftInterestCalculationType',
      ]
    );
    setState(
      this.fixedDepositForm,
      Boolean(this.fixedDepositForm.get('autoRollover')?.value),
      ['rolloverBasis', 'gracePeriod']
    );
    setState(
      this.fixedDepositForm,
      Boolean(this.fixedDepositForm.get('allowPrematureWithdrawal')?.value),
      ['minimumHoldingPeriod', 'penaltyOnInterest', 'noticePeriod']
    );
    setState(
      this.recurringSavingsForm,
      Boolean(this.recurringSavingsForm.get('awardCompletionBonus')?.value),
      ['bonusType', 'bonusValue']
    );
    setState(
      this.recurringSavingsForm,
      Boolean(this.recurringSavingsForm.get('allowEarlyWithdrawal')?.value),
      [
        'earlyWithdrawalPenaltyType',
        'earlyWithdrawalPenaltyValue',
        'maximumSavedPeriodDays',
        'forfeitBonusOnEarlyWithdrawal',
      ]
    );
  }

  invalidateMinimumBalance() {
    const overdraftValue = this.basicForm.get('overdraftLimit').value
    if(overdraftValue) {
      this.otherInfoForm.get('openingBalance').disable()
      this.otherInfoForm.get('openingBalance').removeValidators(Validators.required)
    } else {
      this.otherInfoForm.get('openingBalance').enable()
      this.otherInfoForm.get('openingBalance').addValidators(Validators.required)
    }

    this.otherInfoForm.get('openingBalance').updateValueAndValidity()
  }

  invalidateOverdraftLimit() {
    const openingBalanceValue = this.otherInfoForm.get('openingBalance').value
    if(openingBalanceValue) {
      this.basicForm.get('overdraftLimit').disable()
      this.basicForm.get('overdraftLimit').removeValidators(Validators.required)
    } else {
      this.basicForm.get('overdraftLimit').enable()
      this.basicForm.get('overdraftLimit').addValidators(Validators.required)
    }

    this.basicForm.get('overdraftLimit').updateValueAndValidity()
  }

  validateTransferLimits() {
    const transferLimit = Number(this.utils.sanitizeCurrency(this.otherInfoForm.get('transferLimit')?.value)) ?? null;
    const dailyTransferLimit = Number(this.utils.sanitizeCurrency(this.otherInfoForm.get('dailyTransferLimit')?.value)) ?? null;

    if (
      dailyTransferLimit &&
      transferLimit &&
      transferLimit > dailyTransferLimit
    ) {
      this.toast.error('Transfer limit should not be greater than Daily transfer limit');
      this.isFormValid = false;
      return;
    }

    this.isFormValid = true;
  }

  getPeriodUnitLabel(controlName: string): string {
    switch (this.interestExpenseForm?.get(controlName)?.value) {
      case 'DAY': return $localize`:@@deposit_product_per_day:Per Day`;
      case 'WEEK': return $localize`:@@deposit_product_per_week:Per Week`;
      case 'YEAR': return $localize`:@@deposit_product_per_year:Per Year`;
      default: return $localize`:@@deposit_product_per_month:Per Month`;
    }
  }

  getCalculationTypeLabel(controlName: string): string {
    return this.interestExpenseForm?.get(controlName)?.value === 'MINIMUM_BALANCE'
      ? $localize`:@@minimum_balance:Minimum Balance`
      : $localize`:@@average_balance:Average Balance`;
  }

  getFixedPeriodUnitLabel(controlName: string): string {
    switch (this.fixedDepositForm?.get(controlName)?.value) {
      case 'TENOR': return $localize`:@@deposit_product_per_tenor:Per Tenor`;
      case 'DAY': return $localize`:@@deposit_product_per_day:Per Day`;
      case 'WEEK': return $localize`:@@deposit_product_per_week:Per Week`;
      case 'YEAR': return $localize`:@@deposit_product_per_year:Per Year`;
      default: return $localize`:@@deposit_product_per_month:Per Month`;
    }
  }

  getFixedPayoutLabel(): string {
    switch (this.fixedDepositForm?.get('interestPayout')?.value) {
      case 'QUARTER': return $localize`:@@per_quarter:Per Quarter`;
      case 'MATURITY': return $localize`:@@at_maturity:At Maturity`;
      default: return $localize`:@@deposit_product_per_month:Per Month`;
    }
  }

  getRecurringPeriodUnitLabel(controlName: string): string {
    switch (this.recurringSavingsForm?.get(controlName)?.value) {
      case 'TENOR': return $localize`:@@deposit_product_per_tenor:Per Tenor`;
      case 'DAY': return $localize`:@@deposit_product_per_day:Per Day`;
      case 'WEEK': return $localize`:@@deposit_product_per_week:Per Week`;
      case 'YEAR': return $localize`:@@deposit_product_per_year:Per Year`;
      default: return $localize`:@@deposit_product_per_month:Per Month`;
    }
  }

  getRecurringPayoutLabel(): string {
    switch (this.recurringSavingsForm?.get('interestPayout')?.value) {
      case 'QUARTER': return $localize`:@@per_quarter:Per Quarter`;
      case 'MATURITY': return $localize`:@@at_maturity:At Maturity`;
      default: return $localize`:@@deposit_product_per_month:Per Month`;
    }
  }

  get isMonthlySavingsFrequency(): boolean {
    return this.recurringSavingsForm?.get('savingsFrequency')?.value === 'MONTHLY';
  }

  tierRowLabel(label: string): string {
    return String(label ?? '').replace(/\s*\*+\s*$/, '');
  }

  cancel() {
    this.dialoRef.close()
  }

  approveRejectDepositProduct(state: 'APPROVED' | 'REJECTED'): void {
    if (state === 'APPROVED') {
      const dialogRef = this.dialog.open(ProceedModalComponent, {
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) this.submitApprovalDecision(state);
      });
      return;
    }

    const dialogRef = this.dialog.open(ReasonModalComponent, {
      disableClose: true,
      data: {
        title: $localize`:@@reject_deposit_product:Reject Deposit Product`,
        inputTitle: $localize`:@@reason:Reason`,
        proceedVal: {
          name: $localize`:@@reject:Reject`,
          class: 'p-3 rounded-xl font-semibold opacity-80 hover:opacity-100 text-white w-[150px] border border-error bg-error',
        },
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.submitApprovalDecision(state);
    });
  }

  private submitApprovalDecision(state: 'APPROVED' | 'REJECTED'): void {
    this.depositProductService
      .approveRejectDepositproduct(this.selectedDepositProductId, { state })
      .subscribe((result: any) => {
        if (result?.successful === false) return;
        const message = state === 'APPROVED'
          ? $localize`:@@edited_deposit_product_approved:Edited deposit product approved successfully`
          : $localize`:@@edited_deposit_product_rejected:Edited deposit product rejected successfully`;
        this.toast.success(message);
        this.dialoRef.close(state.toLowerCase());
      });
  }

  submitForm() {
    if (this.basicForm.invalid) {
      this.validator.CheckMessagesAndNotify(this.basicForm.controls);
      this.basicForm.markAllAsTouched();
      return;
    }

    if (this.selectedProductType === 'DEPOSIT_ACCOUNT' && this.interestExpenseForm.invalid) {
      this.validator.CheckMessagesAndNotify(this.interestExpenseForm.controls);
      this.interestExpenseForm.markAllAsTouched();
      return;
    }

    if (this.selectedProductType === 'DEPOSIT_ACCOUNT' && this.otherInfoForm.invalid) {
      this.validator.CheckMessagesAndNotify(this.otherInfoForm.controls);
      this.otherInfoForm.markAllAsTouched();
      return;
    }

    if (this.productInfoForm.invalid) {
      this.validator.CheckMessagesAndNotify(this.productInfoForm.controls);
      this.productInfoForm.markAllAsTouched();
      return;
    }

    const variantForm = this.selectedProductType === 'FIXED_DEPOSIT'
      ? this.fixedDepositForm
      : this.selectedProductType === 'RECURRING_TARGETED'
        ? this.recurringSavingsForm
        : null;

    if (variantForm?.invalid) {
      this.validator.CheckMessagesAndNotify(variantForm.controls);
      variantForm.markAllAsTouched();
      return;
    }

    if (this.feeChargeRows.length > 0 && !this.feeChargeConfig?.isValid()) return;

    this.dragDrop.submit();
  }

  submit(mergedRowArray: any) {
    if (!mergedRowArray?.length) return;
    const payload = this.buildPayload(mergedRowArray);
    console.log(`[Deposit Product] ${this.selectedProductType} payload`, payload);

    if (this.isViewMode) return this.editDepositProduct(payload);
    this.addNewDepositProduct(payload);
  }

  editDepositProduct(payload: any) {
    this.depositProductService.updateDepositProduct(this.selectedDepositProductId, payload).subscribe((result) => {
      if (result?.successful) {
        this.toast.success($localize`:@@deposit_product_updated_successfully:Deposit product updated successfully`);
        this.dialoRef.close(true);
      }
    });
  }

  addNewDepositProduct(payload: any) {
    this.depositProductService.createDepositProduct(payload).subscribe((result) => {
      if (result?.successful) {
        this.toast.success($localize`:@@deposit_product_created_successfully:Deposit product created successfully`);
        this.dialoRef.close(true);
      }
    });
  }

  /**
   * Translate the existing UI form model into the API contract. Keeping this
   * mapping explicit prevents UI-only control names from leaking into payloads.
   */
  private buildPayload(accountingRows: any[]): any {
    const basic = this.basicForm.getRawValue();
    const productInfo = this.productInfoForm.getRawValue();
    const channels = this.channelsForm.getRawValue();
    const depositAccounting = this.accountingPayload(accountingRows);
    const shared = this.sharedPayload(basic, productInfo, channels, depositAccounting);

    if (this.selectedProductType === 'FIXED_DEPOSIT') {
      return { ...shared, ...this.fixedDepositPayload() };
    }

    if (this.selectedProductType === 'RECURRING_TARGETED') {
      return { ...shared, ...this.recurringTargetedPayload() };
    }

    return { ...shared, ...this.depositAccountPayload() };
  }

  private sharedPayload(
    basic: any,
    productInfo: any,
    channels: any,
    depositAccounting: any[]
  ): any {
    const savingsControlAccountId = this.accountingId(
      this.findAccountingId(depositAccounting, 'credit', ['INFLOW']),
      this.findAccountingId(depositAccounting, 'debit', ['OUTFLOW']),
      this.existingProduct.savingsControlAccountId
    );
    const savingsControlAccountName = this.accountName(savingsControlAccountId, depositAccounting);
    const existingSavingsReference = this.existingProduct.savingsReference
      ?? this.existingProduct.savingsReferences
      ?? [];
    const savingsReference = savingsControlAccountId != null
      ? [{
          glAccountId: savingsControlAccountId,
          glAccountName: savingsControlAccountName,
        }]
      : existingSavingsReference.map((reference: any) => ({
          glAccountId: this.numberValue(reference.glAccountId ?? reference.accountId ?? reference.id),
          glAccountName: reference.glAccountName
            || reference.accountName
            || reference.name
            || this.glAccountName(reference.glAccountId ?? reference.accountId ?? reference.id),
        }));

    const openingBalance = this.selectedProductType === 'DEPOSIT_ACCOUNT'
      ? this.numberValue(this.otherInfoForm.getRawValue().openingBalance)
      : this.numberValue(
          this.existingProduct.openingBalance
          ?? this.existingProduct.minimumOpeningBalance
          ?? 0
        );

    return {
      type: this.selectedProductType,
      name: basic.name,
      code: basic.code,
      description: basic.description,
      currency: basic.currency,
      collectionAccounts: this.collectionAccountsPayload(productInfo.fundSources),
      isCalenderDays: productInfo.calculationBasis === 'CALENDAR',
      weekendDays: this.weekendDays(productInfo.weekends),
      roundingMultiple: this.numberValue(productInfo.roundingMultiple),
      enableWebClients: Boolean(channels.webApp),
      enableMobileClients: Boolean(channels.mobileApp),
      enableUssdClients: Boolean(channels.ussd),
      charges: (this.feeChargeRows || [])
        .filter(row => row.chargeId != null)
        .map((row, index) => this.mapRowToApiCharge(row, index)),
      savingsType: this.existingProduct.savingsType ?? 'OPTIONAL',
      autoCreate: this.booleanValue(this.existingProduct.autoCreate),
      openingBalance,
      savingsReference,
      savingsControlAccountId,
      feeIncomeAccountId: this.accountingId(
        this.findAccountingId(depositAccounting, 'credit', ['FEE']),
        this.existingProduct.feeIncomeAccountId
      ),
      penaltyIncomeAccountId: this.accountingId(
        this.findAccountingId(depositAccounting, 'credit', ['PENALTY']),
        this.existingProduct.penaltyIncomeAccountId
      ),
      savingsInterestAccountId: this.accountingId(
        this.findAccountingId(depositAccounting, 'debit', ['INTEREST']),
        this.existingProduct.savingsInterestAccountId
      ),
      writeOffAccountId: this.accountingId(
        this.findAccountingId(depositAccounting, 'debit', ['WRITE_OFF']),
        this.findAccountingId(depositAccounting, 'credit', ['WRITE_OFF']),
        this.existingProduct.writeOffAccountId
      ),
      accountingType: this.existingProduct.accountingType ?? this.activeAccountType,
      depositProductAccounting: depositAccounting,
    };
  }

  private depositAccountPayload(): any {
    const interest = this.interestExpenseForm.getRawValue();
    const limits = this.otherInfoForm.getRawValue();
    const basic = this.basicForm.getRawValue();
    const allowOverdraft = Boolean(this.allowOverdraftControl.value);

    return {
      interestPeriodUnit: interest.interestPeriodUnit,
      defaultInterestRate: this.numberValue(interest.interestRate, 'percentage'),
      minimumInterestRate: this.numberValue(interest.minimumInterestRate, 'percentage'),
      maximumInterestRate: this.numberValue(interest.maximumInterestRate, 'percentage'),
      compoundingPeriodUnit: interest.compoundingPeriodUnit,
      interestPostingPeriodUnit: interest.interestPostingPeriodUnit,
      interestCalculationType: interest.interestCalculationType,
      allowOverdraft,
      overdraftLimit: allowOverdraft ? this.numberValue(basic.overdraftLimit) : null,
      overdraftInterestPeriodUnit: allowOverdraft ? interest.overdraftInterestPeriodUnit : null,
      overdraftInterestRate: allowOverdraft
        ? this.numberValue(interest.overdraftInterestRate, 'percentage')
        : null,
      minimumOverdraftInterestRate: allowOverdraft
        ? this.numberValue(interest.minimumOverdraftInterestRate, 'percentage')
        : null,
      maximumOverdraftInterestRate: allowOverdraft
        ? this.numberValue(interest.maximumOverdraftInterestRate, 'percentage')
        : null,
      overdraftCompoundingPeriodUnit: allowOverdraft
        ? interest.overdraftCompoundingPeriodUnit
        : null,
      overdraftInterestPostingPeriodUnit: allowOverdraft
        ? interest.overdraftInterestPostingPeriodUnit
        : null,
      overdraftInterestCalculationType: allowOverdraft
        ? interest.overdraftInterestCalculationType
        : null,
      minimumBalance: this.numberValue(limits.openingBalance),
      transferLimit: this.numberValue(limits.transferLimit),
      dailyTransferLimit: this.numberValue(limits.dailyTransferLimit),
      dormancyThreshold: this.numberValue(basic.dormancyThreshold, 'plain'),
    };
  }

  private fixedDepositPayload(): any {
    const value = this.fixedDepositForm.getRawValue();
    return {
      fixedDepositDefaultAmount: this.numberValue(value.defaultAmount),
      fixedDepositMinimumAmount: this.numberValue(value.minimumAmount),
      fixedDepositMaximumAmount: this.numberValue(value.maximumAmount),
      fixedDepositDurationPeriodUnit: value.durationPeriodUnit,
      fixedDepositDefaultDuration: this.numberValue(value.defaultDurationDays, 'plain'),
      fixedDepositMinimumDuration: this.numberValue(value.minimumDurationDays, 'plain'),
      fixedDepositMaximumDuration: this.numberValue(value.maximumDurationDays, 'plain'),
      fixedDepositInterestPeriodUnit: value.interestRatePeriodUnit,
      fixedDepositDefaultInterestRate: this.numberValue(value.defaultInterestRate, 'percentage'),
      fixedDepositMinimumInterestRate: this.numberValue(value.minimumInterestRate, 'percentage'),
      fixedDepositMaximumInterestRate: this.numberValue(value.maximumInterestRate, 'percentage'),
      fixedDepositCompoundingPeriodUnit: value.compoundingFrequency,
      fixedDepositInterestPayoutPeriodUnit: this.toApiPayoutPeriod(value.interestPayout),
      fixedDepositAutoRolloverAtMaturity: Boolean(value.autoRollover),
      fixedDepositRolloverBasis: value.autoRollover ? value.rolloverBasis : null,
      fixedDepositRolloverGracePeriodDays: value.autoRollover
        ? this.numberValue(value.gracePeriod, 'plain')
        : null,
      fixedDepositAllowPrematureWithdrawal: Boolean(value.allowPrematureWithdrawal),
      fixedDepositMinimumHoldingPeriodDays: value.allowPrematureWithdrawal
        ? this.numberValue(value.minimumHoldingPeriod, 'plain')
        : null,
      fixedDepositPrematureWithdrawalPenaltyType: value.allowPrematureWithdrawal
        ? value.prematureWithdrawalPenaltyType
        : null,
      fixedDepositPrematureWithdrawalPenaltyOnInterest: value.allowPrematureWithdrawal
        ? this.numberValue(value.penaltyOnInterest, 'percentage')
        : null,
      fixedDepositPrematureWithdrawalNoticePeriodDays: value.allowPrematureWithdrawal
        ? this.numberValue(value.noticePeriod, 'plain')
        : null,
    };
  }

  private recurringTargetedPayload(): any {
    const value = this.recurringSavingsForm.getRawValue();
    return {
      recurringDefaultTargetAmount: this.numberValue(value.defaultTargetAmount),
      recurringMinimumTargetAmount: this.numberValue(value.minimumTargetAmount),
      recurringMaximumTargetAmount: this.numberValue(value.maximumTargetAmount),
      recurringDurationPeriodUnit: value.durationPeriodUnit,
      recurringDefaultDuration: this.numberValue(value.defaultDurationMonths, 'plain'),
      recurringMinimumDuration: this.numberValue(value.minimumDurationMonths, 'plain'),
      recurringMaximumDuration: this.numberValue(value.maximumDurationMonths, 'plain'),
      recurringInterestPeriodUnit: value.interestRatePeriodUnit,
      recurringDefaultInterestRate: this.numberValue(value.defaultInterestRate, 'percentage'),
      recurringMinimumInterestRate: this.numberValue(value.minimumInterestRate, 'percentage'),
      recurringMaximumInterestRate: this.numberValue(value.maximumInterestRate, 'percentage'),
      recurringCompoundingPeriodUnit: value.compoundingFrequency,
      recurringInterestPayoutPeriodUnit: this.toApiPayoutPeriod(value.interestPayout),
      recurringSavingsFrequency: this.toApiSavingsFrequency(value.savingsFrequency),
      ...(value.savingsFrequency === 'MONTHLY'
        ? { recurringDayOfMonth: this.numberValue(value.recurringDayOfMonth, 'plain') }
        : {}),
      recurringContributionSource: this.toApiContributionSource(value.contributionSource),
      recurringGracePeriodDays: this.numberValue(value.gracePeriod, 'plain'),
      recurringBonusOnCompletionEnabled: Boolean(value.awardCompletionBonus),
      recurringBonusType: value.awardCompletionBonus ? value.bonusType : null,
      recurringBonusValue: value.awardCompletionBonus
        ? this.numberValue(value.bonusValue, value.bonusType === 'PERCENTAGE' ? 'percentage' : 'currency')
        : null,
      recurringAllowEarlyWithdrawal: Boolean(value.allowEarlyWithdrawal),
      recurringEarlyWithdrawalMinimumLockPeriodDays: value.allowEarlyWithdrawal
        ? this.numberValue(value.maximumSavedPeriodDays, 'plain')
        : null,
      recurringEarlyWithdrawalPenaltyType: value.allowEarlyWithdrawal
        ? value.earlyWithdrawalPenaltyType
        : null,
      recurringEarlyWithdrawalPenaltyValue: value.allowEarlyWithdrawal
        ? this.numberValue(
            value.earlyWithdrawalPenaltyValue,
            value.earlyWithdrawalPenaltyType === 'PERCENTAGE' ? 'percentage' : 'currency'
          )
        : null,
      recurringMissedContributionPenaltyType: value.missedContributionPenaltyType,
      recurringMissedContributionPenaltyValue: this.numberValue(
        value.missedContributionPenaltyValue,
        value.missedContributionPenaltyType === 'PERCENTAGE' ? 'percentage' : 'currency'
      ),
      recurringForfeitBonusOnMissedContribution: Boolean(value.forfeitBonusOnMissedContribution),
      recurringAtCompletionAction: this.toApiCompletionAction(value.glTemplate),
    };
  }

  disableAllFields() {
    this.productTypeControl.disable();
    this.allowOverdraftControl.disable();
    this.basicForm.disable();

    this.interestExpenseForm.disable();
    this.interestExpenseForm.get('interestPostingPeriodUnit')?.disable()
    this.interestExpenseForm.get('overdraftInterestPostingPeriodUnit')?.disable()

    this.otherInfoForm.disable();
    this.productInfoForm.disable();
    this.channelsForm.disable();
    this.fixedDepositForm.disable();
    this.recurringSavingsForm.disable();
    this.isDisabled = true;
  }

  enableAllFields() {
    this.productTypeControl.enable();
    this.allowOverdraftControl.enable();
    this.basicForm.enable();

    this.interestExpenseForm.enable();

    this.otherInfoForm.enable();
    this.productInfoForm.enable();
    this.channelsForm.enable();
    this.fixedDepositForm.enable();
    this.recurringSavingsForm.enable();
    this.refreshSwitchDependencies();
    this.isDisabled = false;
  }

  editForm(type: 'edit' | 'save') {
    if (type === 'edit') return this.enableAllFields();
    this.submitForm()
  }

  chargeIdChange(selectedIds: number[]) {
    this.dragDrop.chargeIdChange(selectedIds);
  }

  ngOnInit(): void {
    this.setupTenureRules();
    this.onProductTypeChange(this.productTypeControl.value || 'DEPOSIT_ACCOUNT');
    this.productTypeControl.valueChanges.subscribe(productType => {
      if (productType) this.onProductTypeChange(productType);
    });

    this.loadFeeEventTypes();
    this.setupDepositCalculatorProjection();
    this.setupFixedDepositCalculatorProjection();
    this.setupRecurringTargetedCalculatorProjection();
    if (
      this.isViewMode
      && this.loadProductOnInit
      && this.selectedDepositProductId
      && !this.feeChargeRows.length
    ) {
      this.loadFullDepositProduct();
    }
  }

  private setupDepositCalculatorProjection(): void {
    const calculatorControls = [
      'calculatorAverageBalance',
      'calculatorInterestRate',
      'minimumInterestRate',
      'maximumInterestRate',
      'interestPeriodUnit',
      'compoundingPeriodUnit',
      'interestPostingPeriodUnit',
    ]
      .map(name => this.interestExpenseForm.get(name)?.valueChanges)
      .filter(Boolean);
    const minimumBalanceChanges = this.otherInfoForm.get('openingBalance')?.valueChanges;
    if (minimumBalanceChanges) calculatorControls.push(minimumBalanceChanges);

    merge(...calculatorControls)
      .pipe(
        debounceTime(700),
        filter(() => this.hasCompleteDepositCalculatorInput()),
        switchMap(() => {
          const calculator = this.interestExpenseForm.getRawValue();
          const payload = {
            averageBalance: this.numberValue(calculator.calculatorAverageBalance) as number,
            interestRate: this.numberValue(calculator.calculatorInterestRate, 'percentage') as number,
            interestRatePeriodUnit: calculator.interestPeriodUnit,
            compoundingPeriodUnit: calculator.compoundingPeriodUnit,
            interestPostingPeriodUnit: calculator.interestPostingPeriodUnit,
          };

          return this.depositProductService.calculateDepositAccountProjection(payload).pipe(
            catchError(error => {
              this.depositProjectedInterest = null;
              return EMPTY;
            })
          );
        })
      )
      .subscribe(response => {
        this.depositProjectedInterest = response?.successful === false
          ? null
          : this.numberValue(response?.data?.projectedInterest);
      });
  }

  private hasCompleteDepositCalculatorInput(): boolean {
    if (this.selectedProductType !== 'DEPOSIT_ACCOUNT') return false;

    const calculator = this.interestExpenseForm.getRawValue();
    const averageBalance = this.numberValue(calculator.calculatorAverageBalance);
    const interestRate = this.numberValue(calculator.calculatorInterestRate, 'percentage');

    return averageBalance != null
      && averageBalance > 0
      && interestRate != null
      && interestRate > 0
      && Boolean(calculator.interestPeriodUnit)
      && Boolean(calculator.compoundingPeriodUnit)
      && Boolean(calculator.interestPostingPeriodUnit);
  }

  /** Compounding cannot be longer than the tenure (Tenor, once for the whole tenure, always fits) */
  private setupTenureRules(): void {
    [this.fixedDepositForm, this.recurringSavingsForm].forEach(form => {
      form.get('durationPeriodUnit')?.valueChanges.subscribe(() => this.clampCompoundingToTenure(form));
    });
  }

  private clampCompoundingToTenure(form: FormGroup): void {
    const compounding = form.get('compoundingFrequency');
    const clamped = compoundingForTenure(compounding?.value, form.get('durationPeriodUnit')?.value);
    if (compounding && clamped !== compounding.value) {
      compounding.setValue(clamped);
    }
  }

  get fixedCompoundingOptions() {
    return compoundingOptionsFor(
      this.fixedDepositForm?.get('durationPeriodUnit')?.value,
      this.fixedDepositForm?.get('compoundingFrequency')?.value,
    );
  }

  get recurringCompoundingOptions() {
    return compoundingOptionsFor(
      this.recurringSavingsForm?.get('durationPeriodUnit')?.value,
      this.recurringSavingsForm?.get('compoundingFrequency')?.value,
    );
  }

  get recurringTenureUnitOptions() {
    return tenureUnitsForFrequency(
      this.recurringSavingsForm?.get('savingsFrequency')?.value,
      this.recurringSavingsForm?.get('durationPeriodUnit')?.value,
    );
  }

  /** "days", "months" ... for labels that name the tenure unit */
  fixedTenureUnitLabel(): string {
    return tenureUnitLabel(this.fixedDepositForm?.get('durationPeriodUnit')?.value).toLowerCase();
  }

  recurringTenureUnitLabel(): string {
    return tenureUnitLabel(this.recurringSavingsForm?.get('durationPeriodUnit')?.value).toLowerCase();
  }

  private setupFixedDepositCalculatorProjection(): void {
    const calculatorControls = [
      'calculatorAmount',
      'calculatorDuration',
      'calculatorInterestRate',
      'earlyWithdrawalDay',
      'minimumAmount',
      'maximumAmount',
      'minimumDurationDays',
      'maximumDurationDays',
      'minimumInterestRate',
      'maximumInterestRate',
      'durationPeriodUnit',
      'interestRatePeriodUnit',
      'compoundingFrequency',
      'allowPrematureWithdrawal',
      'prematureWithdrawalPenaltyType',
      'penaltyOnInterest',
    ]
      .map(name => this.fixedDepositForm.get(name)?.valueChanges)
      .filter(Boolean);
    calculatorControls.push(this.productTypeControl.valueChanges);

    merge(...calculatorControls)
      .pipe(
        debounceTime(700),
        switchMap(() => {
          if (!this.hasCompleteFixedDepositCalculatorInput()) {
            this.fixedDepositCalculatorResult = null;
            return EMPTY;
          }

          const calculator = this.fixedDepositForm.getRawValue();
          const penaltyFormat = calculator.prematureWithdrawalPenaltyType === 'PERCENTAGE'
            ? 'percentage'
            : 'currency';
          const allowPrematureWithdrawal = Boolean(calculator.allowPrematureWithdrawal);
          const payload = {
            amount: this.numberValue(calculator.calculatorAmount) as number,
            duration: this.numberValue(calculator.calculatorDuration, 'plain') as number,
            durationPeriodUnit: calculator.durationPeriodUnit,
            interestRate: this.numberValue(calculator.calculatorInterestRate, 'percentage') as number,
            interestPeriodUnit: calculator.interestRatePeriodUnit,
            compoundingPeriodUnit: calculator.compoundingFrequency,
            earlyWithdrawalDay:
              this.numberValue(calculator.earlyWithdrawalDay, 'plain') ?? 0,
            prematureWithdrawalPenaltyType: calculator.prematureWithdrawalPenaltyType,
            prematureWithdrawalPenaltyOnInterest: allowPrematureWithdrawal
              ? this.numberValue(calculator.penaltyOnInterest, penaltyFormat) ?? 0
              : 0,
          };

          return this.depositProductService.calculateFixedDepositProjection(payload).pipe(
            catchError(error => {
              this.fixedDepositCalculatorResult = null;
              return EMPTY;
            })
          );
        })
      )
      .subscribe(response => {
        this.fixedDepositCalculatorResult = response?.successful === false
          ? null
          : response?.data ?? null;
      });
  }

  private hasCompleteFixedDepositCalculatorInput(): boolean {
    if (this.selectedProductType !== 'FIXED_DEPOSIT') return false;

    const calculator = this.fixedDepositForm.getRawValue();
    const amount = this.numberValue(calculator.calculatorAmount);
    const duration = this.numberValue(calculator.calculatorDuration, 'plain');
    const interestRate = this.numberValue(calculator.calculatorInterestRate, 'percentage');

    return amount != null
      && amount > 0
      && duration != null
      && duration > 0
      && interestRate != null
      && interestRate > 0
      && Boolean(calculator.durationPeriodUnit)
      && Boolean(calculator.interestRatePeriodUnit)
      && Boolean(calculator.compoundingFrequency)
      && Boolean(calculator.prematureWithdrawalPenaltyType);
  }

  private setupRecurringTargetedCalculatorProjection(): void {
    const calculatorControls = [
      'calculatorTargetAmount',
      'calculatorDuration',
      'calculatorInterestRate',
      'durationPeriodUnit',
      'defaultInterestRate',
      'interestRatePeriodUnit',
      'savingsFrequency',
      'awardCompletionBonus',
      'bonusType',
      'bonusValue',
      'minimumTargetAmount',
      'maximumTargetAmount',
      'minimumDurationMonths',
      'maximumDurationMonths',
      'minimumInterestRate',
      'maximumInterestRate',
    ]
      .map(name => this.recurringSavingsForm.get(name)?.valueChanges)
      .filter(Boolean);

    merge(...calculatorControls)
      .pipe(
        debounceTime(700),
        switchMap(() => {
          if (!this.hasCompleteRecurringCalculatorInput()) {
            this.recurringCalculatorResult = null;
            return EMPTY;
          }

          const value = this.recurringSavingsForm.getRawValue();
          const interestRate = this.recurringCalculatorInterestRate(value);
          const payload = {
            targetAmount: this.numberValue(value.calculatorTargetAmount) as number,
            duration: this.numberValue(value.calculatorDuration, 'plain') as number,
            durationPeriodUnit: value.durationPeriodUnit,
            interestRate,
            interestPeriodUnit: value.interestRatePeriodUnit,
            savingsFrequency: this.toApiSavingsFrequency(value.savingsFrequency),
            bonusOnCompletionEnabled: Boolean(value.awardCompletionBonus),
            bonusType: value.bonusType,
            bonusValue: this.numberValue(
              value.bonusValue,
              value.bonusType === 'PERCENTAGE' ? 'percentage' : 'currency'
            ) as number,
          };

          return this.depositProductService
            .calculateRecurringTargetedProjection(payload)
            .pipe(
              catchError(error => {
                this.recurringCalculatorResult = null;
                return EMPTY;
              })
            );
        })
      )
      .subscribe(response => {
        this.recurringCalculatorResult = response?.successful === false
          ? null
          : response?.data ?? null;
      });
  }

  public formatProjectionAmount(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return this.currencyFormatter.formatCurrency(value);
  }

  private hasCompleteRecurringCalculatorInput(): boolean {
    if (this.selectedProductType !== 'RECURRING_TARGETED') return false;

    const value = this.recurringSavingsForm.getRawValue();
    const targetAmount = this.numberValue(value.calculatorTargetAmount);
    const duration = this.numberValue(value.calculatorDuration, 'plain');
    const interestRate = this.recurringCalculatorInterestRate(value);
    const bonusValue = this.numberValue(
      value.bonusValue,
      value.bonusType === 'PERCENTAGE' ? 'percentage' : 'currency'
    );

    return targetAmount != null
      && targetAmount > 0
      && duration != null
      && duration > 0
      && interestRate != null
      && interestRate > 0
      && bonusValue != null
      && bonusValue >= 0
      && Boolean(value.durationPeriodUnit)
      && Boolean(value.interestRatePeriodUnit)
      && Boolean(value.savingsFrequency)
      && Boolean(value.bonusType);
  }

  /** The visible calculator value is what the user is testing. The configured
      default rate only seeds/falls back when that calculator input is empty. */
  private recurringCalculatorInterestRate(value: any): number | null {
    const calculatorRate = this.numberValue(
      value.calculatorInterestRate,
      'percentage'
    );
    if (calculatorRate != null && calculatorRate > 0) return calculatorRate;

    return this.numberValue(value.defaultInterestRate, 'percentage');
  }

  private isWithinConfiguredRange(
    value: number,
    minimum: number | null,
    maximum: number | null
  ): boolean {
    return (minimum == null || value >= minimum)
      && (maximum == null || value <= maximum);
  }

  loadFeeEventTypes() {
    this.depositProductService.getFeeEvents().subscribe(res => {
      if (res?.data?.length) {
        this.feeEventTypes = res.data
          .filter((e: FeeEventType) => e.appliesTo.includes('SAVINGS'))
          .map((e: FeeEventType) => ({ label: e.label, value: e.event, scheduleType: e.scheduleType }));
      }
    });
  }

  loadFullDepositProduct() {
    this.depositProductService.getDepositProduct(this.selectedDepositProductId).subscribe((res: any) => {
      const product = res?.data;
      if (!product) return;
      this.existingProduct = { ...this.existingProduct, ...product };
      this.patchDepositAccountInterestFields(product);
      const savingsProductCharges: DepositProductFeeCharge[] = product.savingsProductCharges
        ?? product.charges
        ?? [];
      if (savingsProductCharges?.length) {
        this.feeChargeRows = [...savingsProductCharges]
          .sort((firstCharge, secondCharge) => firstCharge.sortOrder - secondCharge.sortOrder)
          .map((charge: DepositProductFeeCharge) => this.mapApiChargeToRow(charge));
      }
    });
  }

  /** The list/table record passed into the dialog can omit the interest
      schedule. Always repopulate these controls from the full-product GET. */
  private patchDepositAccountInterestFields(product: any): void {
    if (!product || !this.interestExpenseForm) return;

    this.interestExpenseForm.patchValue({
      interestRate:
        product.defaultInterestRate ?? product.interestRate ?? '',
      minimumInterestRate:
        product.minimumInterestRate ?? product.minInterestRate ?? '',
      maximumInterestRate:
        product.maximumInterestRate ?? product.maxInterestRate ?? '',
      interestPeriodUnit:
        product.interestPeriodUnit ??
        product.interestRatePeriodUnit ??
        'MONTH',
      compoundingPeriodUnit:
        product.compoundingPeriodUnit ??
        product.compoundingFrequency ??
        'MONTH',
      interestPostingPeriodUnit:
        product.interestPostingPeriodUnit ??
        product.postingPeriodUnit ??
        'MONTH',
      interestCalculationType:
        product.interestCalculationType ??
        product.interestCalculation ??
        'AVERAGE_BALANCE',

      overdraftInterestRate: product.overdraftInterestRate ?? '',
      minimumOverdraftInterestRate:
        product.minimumOverdraftInterestRate ??
        product.overdraftMinimumInterestRate ??
        '',
      maximumOverdraftInterestRate:
        product.maximumOverdraftInterestRate ??
        product.overdraftMaximumInterestRate ??
        '',
      overdraftInterestPeriodUnit:
        product.overdraftInterestPeriodUnit ?? 'MONTH',
      overdraftCompoundingPeriodUnit:
        product.overdraftCompoundingPeriodUnit ?? 'MONTH',
      overdraftInterestPostingPeriodUnit:
        product.overdraftInterestPostingPeriodUnit ?? 'MONTH',
      overdraftInterestCalculationType:
        product.overdraftInterestCalculationType ?? 'AVERAGE_BALANCE',
    });
  }

  syncFeeCharges(rows: FeeChargeRow[]) {
    this.feeChargeRows = rows;
    // Pass '' so deposit fee rows have no pre-selected event — the user must
    // choose one valid for deposit (DEPOSIT, WITHDRAWAL, CRON, CRON_OVERDRAFT).
    // 'DISBURSEMENT-CHARGE_APPLIED' is the loan default and is rejected by the BE.
    this.dragDrop.syncFeeChargeRows(rows, this.charges, '');
  }

  private mapApiChargeToRow(c: DepositProductFeeCharge): FeeChargeRow {
    const flatCharge = c as any;
    const chargeId = c.charge?.id ?? flatCharge.chargeId;
    const selectedCharge = this.charges?.find(charge => charge.id === chargeId);
    const isMonthlyCustom = c.event === 'MONTHLY'
      && c.scheduleDetail
      && !['FIRST_DAY', 'LAST_DAY'].includes(c.scheduleDetail);

    return {
      id: c.id,
      chargeId,
      chargeType: c.charge?.chargeType ?? selectedCharge?.chargeType ?? '',
      value: (c.chargeAmount ?? flatCharge.value) != null
        ? String(c.chargeAmount ?? flatCharge.value)
        : '',
      minimumValue: c.minValue != null ? String(c.minValue) : '',
      maximumValue: c.maxValue != null ? String(c.maxValue) : '',
      eventType: c.event ?? '',
      eventSubValue: isMonthlyCustom ? 'CUSTOM' : (c.scheduleDetail ?? ''),
      monthlyCustomDay: isMonthlyCustom ? (c.scheduleDetail ?? '') : '',
      q1Month: c.quarterOneMonth ?? '',
      q2Month: c.quarterTwoMonth ?? '',
      q3Month: c.quarterThreeMonth ?? '',
      q4Month: c.quarterFourMonth ?? '',
      h1Month: c.firstHalfMonth ?? '',
      h2Month: c.secondHalfMonth ?? '',
    };
  }

  private mapRowToApiCharge(row: FeeChargeRow, index: number): any {
    const charge: any = {
      ...(row.id != null ? { id: this.numberValue(row.id, 'plain') } : {}),
      chargeId: this.numberValue(row.chargeId, 'plain'),
      value: this.numberValue(row.value),
      event: row.eventType,
      sortOrder: index + 1,
    };

    const minValue = this.numberValue(row.minimumValue);
    const maxValue = this.numberValue(row.maximumValue);
    if (minValue != null) charge.minValue = minValue;
    if (maxValue != null) charge.maxValue = maxValue;

    if (['DAILY', 'WEEKLY', 'ANNUALLY'].includes(row.eventType)) {
      if (row.eventSubValue) charge.scheduleDetail = row.eventSubValue;
    } else if (row.eventType === 'MONTHLY') {
      const scheduleDetail = row.eventSubValue === 'CUSTOM'
        ? row.monthlyCustomDay
        : row.eventSubValue;
      if (scheduleDetail) charge.scheduleDetail = scheduleDetail;
    } else if (row.eventType === 'QUARTERLY') {
      charge.quarterOneMonth = row.q1Month;
      charge.quarterTwoMonth = row.q2Month;
      charge.quarterThreeMonth = row.q3Month;
      charge.quarterFourMonth = row.q4Month;
    } else if (row.eventType === 'SEMI_ANNUALLY') {
      charge.firstHalfMonth = row.h1Month;
      charge.secondHalfMonth = row.h2Month;
    }

    return charge;
  }

  private weekendDays(value: string): string[] {
    return value ? value.split('_') : [];
  }

  private collectionAccountsPayload(ids: number[]): any[] {
    return (ids || []).map(id => ({
      accountId: this.numberValue(id, 'plain'),
      accountName: this.fundSourceName(id),
    }));
  }

  private accountingPayload(rows: any[]): any[] {
    return (rows || []).map(row => ({
      ...(row.id != null ? { id: this.numberValue(row.id, 'plain') } : {}),
      label: row.label,
      eventName: row.eventName,
      eventMapping: row.eventMapping,
      groupLabel: row.groupLabel,
      debitGlId: this.numberValue(row.debitGlId, 'plain'),
      debitGlName: row.debitGlName || this.glAccountName(row.debitGlId),
      creditGlId: this.numberValue(row.creditGlId, 'plain'),
      creditGlName: row.creditGlName || this.glAccountName(row.creditGlId),
      ...(row.chargeId != null ? { chargeId: this.numberValue(row.chargeId, 'plain') } : {}),
    }));
  }

  private findAccountingId(
    rows: any[],
    side: 'debit' | 'credit',
    matchingTokens: string[]
  ): number | null {
    const row = rows.find(item => {
      const eventName = String(item.eventName ?? '').toUpperCase();
      const eventMapping = String(item.eventMapping ?? '').toUpperCase();
      const label = String(item.label ?? '').toUpperCase();
      return matchingTokens.some(token =>
        eventName === token || eventMapping === token || label.includes(token)
      );
    });
    return row ? this.numberValue(row[`${side}GlId`], 'plain') : null;
  }

  private accountingId(...candidates: any[]): number | null {
    for (const candidate of candidates) {
      const value = this.numberValue(candidate, 'plain');
      if (value != null) return value;
    }
    return null;
  }

  private accountName(accountId: number | null, accountingRows: any[]): string {
    if (accountId == null) return '';
    const row = accountingRows.find(item =>
      item.debitGlId === accountId || item.creditGlId === accountId
    );
    if (row?.debitGlId === accountId && row.debitGlName) return row.debitGlName;
    if (row?.creditGlId === accountId && row.creditGlName) return row.creditGlName;
    return this.glAccountName(accountId);
  }

  private glAccountName(accountId: any): string {
    const id = this.numberValue(accountId, 'plain');
    return this.glAccounts?.find(account => account.id === id)?.name ?? '';
  }

  private numberValue(
    value: any,
    format: 'currency' | 'percentage' | 'plain' = 'currency'
  ): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    let normalized: any = String(value).trim();
    if (!normalized) return null;
    if (format === 'currency') normalized = this.currencyFormatter.parseCurrency(normalized);
    if (format === 'percentage') normalized = this.utils.sanitizePercentage(normalized);
    if (format === 'plain') normalized = normalized.replace(/,/g, '');

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private booleanValue(value: any, fallback = false): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.toLowerCase());
    return fallback;
  }

  private normalizeProductType(value: string): string {
    return value === 'RECURRING_SAVINGS' ? 'RECURRING_TARGETED' : (value || 'DEPOSIT_ACCOUNT');
  }

  private toApiPayoutPeriod(value: string): string {
    return value === 'MATURITY' ? 'TENOR' : value;
  }

  private toUiPayoutPeriod(value: string): string {
    return value === 'TENOR' ? 'MATURITY' : (value || 'MONTH');
  }

  private toApiSavingsFrequency(value: string): string {
    if (value === 'WEEKLY') return 'WEEK';
    if (value === 'MONTHLY') return 'MONTH';
    return value;
  }

  private toUiSavingsFrequency(value: string): string {
    if (value === 'WEEK') return 'WEEKLY';
    if (value === 'MONTH') return 'MONTHLY';
    return value || 'MONTHLY';
  }

  private toApiContributionSource(value: string): string {
    if (value === 'AUTO_DEBIT') return 'AUTO_DEBIT_LINKED_ACCOUNT';
    if (value === 'MANUAL') return 'MANUAL_DEPOSIT';
    return value;
  }

  private toUiContributionSource(value: string): string {
    if (value === 'AUTO_DEBIT_LINKED_ACCOUNT') return 'AUTO_DEBIT';
    if (value === 'MANUAL_DEPOSIT') return 'MANUAL';
    return value || 'AUTO_DEBIT';
  }

  private toApiCompletionAction(value: string): string {
    return value === 'USE_LINKED_SAVINGS_ACCOUNT'
      ? 'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT'
      : value;
  }

  private toUiCompletionAction(value: string): string {
    return value === 'TRANSFER_TO_LINKED_SAVINGS_ACCOUNT'
      ? 'USE_LINKED_SAVINGS_ACCOUNT'
      : (value || 'USE_LINKED_SAVINGS_ACCOUNT');
  }

}
